import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Grid, Typography, Alert, Skeleton, Card, CardContent, Box, Paper, Button, useMediaQuery, useTheme, Drawer, IconButton, Collapse, List, ListItem, ListItemText, Checkbox, TextField, InputAdornment } from '@mui/material';
import ProductCard from '../product/ProductCard';
import { search } from '@api/search';
import ErrorBoundary from '../error/ErrorBoundary';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';


// Product card skeleton for CategoryPage
function CategoryProductSkeleton() {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '4px 4px 0 0' }} />
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={28} width="40%" sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );
}

// Improved facet sidebar skeleton
function FacetSidebarSkeleton() {
  return (
    <Box sx={{ width: '100%', mb: { xs: 3, sm: 0 } }}>
      <Skeleton variant="text" height={32} width="60%" sx={{ mb: 2 }} />
      {[1, 2, 3, 4].map((item) => (
        <Box key={item} sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          <Box sx={{ pl: 1 }}>
            {[1, 2, 3, 4].map((subItem) => (
              <Box key={subItem} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Skeleton variant="rectangular" width={20} height={20} sx={{ mr: 1, borderRadius: 0.5 }} />
                <Skeleton variant="text" height={20} sx={{ flex: 1, maxWidth: '70%' }} />
                <Skeleton variant="text" height={16} width={24} sx={{ ml: 'auto' }} />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// Utility function to normalize facets for Fusion/Cloud and Solr formats
function normalizeFacets(facets) {
  if (!facets) return null;
  let facetFields = null;
  if (facets.fields) {
    facetFields = {};
    Object.entries(facets.fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  } else if (facets.facet_fields) {
    facetFields = {};
    Object.entries(facets.facet_fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  }
  return facetFields;
}

// Memoized FacetSidebar with optimized search
const FacetSidebar = React.memo(({ facets, selectedFacets, onFacetChange }) => {
  const [openFacets, setOpenFacets] = useState(() => {
    // Initialize all facets as open
    const initialState = {};
    Object.keys(facets || {}).forEach(field => {
      initialState[field] = true;
    });
    return initialState;
  });
  const [facetSearchTerms, setFacetSearchTerms] = useState({});

  const searchTimeouts = useRef({});

  const handleFacetSearch = useCallback((field, searchTerm) => {
    // Clear existing timeout
    if (searchTimeouts.current[field]) {
      clearTimeout(searchTimeouts.current[field]);
    }

    // Set new timeout for debounced search
    searchTimeouts.current[field] = setTimeout(() => {
      setFacetSearchTerms(prev => ({
        ...prev,
        [field]: searchTerm
      }));
    }, 300);
  }, []);

  // Optimized bucket filtering with useMemo
  const facetBuckets = useMemo(() => {
    const result = {};
    Object.entries(facets || {}).forEach(([field, facet]) => {
      const searchTerm = facetSearchTerms[field]?.toLowerCase() || '';
      const buckets = facet.buckets || [];
      
      result[field] = {
        filtered: searchTerm 
          ? buckets.filter(bucket => bucket.val.toLowerCase().includes(searchTerm))
          : buckets,
        hasSearchResults: searchTerm ? buckets.some(bucket => bucket.val.toLowerCase().includes(searchTerm)) : true
      };
    });
    return result;
  }, [facets, facetSearchTerms]);

  const toggleFacet = useCallback((field) => {
    setOpenFacets(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  if (!facets) return null;

  return (
    <Box sx={{ width: '100%', mb: { xs: 3, sm: 0 } }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
        Filter Results
      </Typography>
      {Object.entries(facets).map(([field, facet]) => {
        const { filtered: filteredBuckets, hasSearchResults } = facetBuckets[field] || { filtered: [], hasSearchResults: false };
        
        return (
          <Box key={field} sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                py: 1,
                borderBottom: '1px solid #eee',
                backgroundColor: 'primary.main',
                px: 1,
                borderRadius: '4px 4px 0 0',
                color: 'white',
              }}
              onClick={() => toggleFacet(field)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, textTransform: 'capitalize', color: 'white' }}>
                {field.replace(/_/g, ' ')}
              </Typography>
              {openFacets[field] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
            <Collapse in={!!openFacets[field]} timeout="auto" unmountOnExit>
              <Box sx={{ p: 1, borderLeft: '1px solid #eee', borderRight: '1px solid #eee', bgcolor: '#fafafa' }}>
                {/* Facet search input - only show for facets with more than 5 options */}
                {Array.isArray(facet?.buckets) && facet.buckets.length > 5 && (
                  <TextField
                    size="small"
                    placeholder={`Search ${field.replace(/_/g, ' ')}...`}
                    onChange={(e) => handleFacetSearch(field, e.target.value)}
                    sx={{ 
                      width: '100%', 
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '0.875rem'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                
                <List dense disablePadding sx={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {Array.isArray(facet?.buckets) && facet.buckets.length > 0 ? (
                    hasSearchResults ? (
                      filteredBuckets.map((bucket) => (
                        <ListItem key={`${field}-${bucket.val}`} disableGutters sx={{ pl: 0 }}>
                          <Checkbox
                            size="small"
                            checked={selectedFacets[field]?.includes(bucket.val) || false}
                            onChange={() => onFacetChange(field, bucket.val)}
                            color="primary"
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography noWrap sx={{ fontSize: 15, maxWidth: '80%' }}>
                                  {bucket.val}
                                </Typography>
                                <Typography sx={{ color: '#888', fontWeight: 400, fontSize: 14 }}>
                                  ({bucket.count})
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disableGutters sx={{ pl: 0 }}>
                        <ListItemText primary={
                          <Typography sx={{ color: '#888', fontSize: 14, fontStyle: 'italic' }}>
                            No matches found
                          </Typography>
                        } />
                      </ListItem>
                    )
                  ) : (
                    <ListItem disableGutters sx={{ pl: 0 }}>
                      <ListItemText primary={<span style={{ color: '#888', fontSize: 14 }}>No options</span>} />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
});

// Memoized product grid component
const ProductGrid = React.memo(({ products, loading }) => {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: 24 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={`skeleton-${index}`}>
            <CategoryProductSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (products.length === 0) {
    return (
      <Typography>No products found in this category.</Typography>
    );
  }

  return (
    <Grid container spacing={3}>
      {products.map((product, idx) => (
        <Grid 
          item 
          xs={12} 
          sm={6} 
          md={4} 
          lg={4} 
          key={product.id ? String(product.id) : product.sku ? `sku-${product.sku}` : `product-${idx}`}
        >
          <ErrorBoundary>
            <ProductCard product={product} />
          </ErrorBoundary>
        </Grid>
      ))}
    </Grid>
  );
});

function CategoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Memoized category extraction
  const categoryName = useMemo(() => {
    const params = new URLSearchParams(location.search);
    // Support both ?Lighting and ?category=Lighting
    if (params.has('category')) return params.get('category');
    
    // Handle case where category is a key without value (e.g., ?Lighting&facets=...)
    for (const [key, value] of params.entries()) {
      if (value === '' && key !== 'facets' && key !== 'page') {
        return decodeURIComponent(key);
      }
    }
    
    // If query string is like ?Lighting (no key, just value)
    const search = location.search.replace(/^\?/, '').split('&')[0];
    if (search && !search.includes('=') && !search.startsWith('facets=')) {
      return decodeURIComponent(search);
    }
    return '';
  }, [location.search]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numFound, setNumFound] = useState(0);
  const [facets, setFacets] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Memoized display name
  const displayCategoryName = useMemo(() => {
    return categoryName
      ? categoryName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : '';
  }, [categoryName]);

  // Parse facets from URL - optimized
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const facetsParam = params.get('facets');
    
    if (facetsParam) {
      const facetObj = {};
      facetsParam.split('|').forEach(f => {
        const [field, vals] = f.split(':');
        if (field && vals) {
          facetObj[field] = vals.split('~');
        }
      });
      setSelectedFacets(facetObj);
    } else {
      setSelectedFacets({});
    }
  }, [location.search]);

  // Memoized filter queries builder
  const filterQueries = useMemo(() => {
    const queries = [`category:("${categoryName}")`];
    
    Object.entries(selectedFacets).forEach(([field, values]) => {
      if (values && values.length > 0) {
        values.forEach(val => queries.push(`${field}:"${val}"`));
      }
    });
    
    return queries;
  }, [categoryName, selectedFacets]);

  // Optimized facet change handler
  const handleFacetChange = useCallback((field, value) => {
    const current = selectedFacets[field] || [];
    const newSelected = current.includes(value) 
      ? current.filter(v => v !== value)
      : [...current, value];
    
    const updatedFacets = { ...selectedFacets, [field]: newSelected };
    
    // Remove empty arrays
    Object.keys(updatedFacets).forEach(k => {
      if (!updatedFacets[k] || updatedFacets[k].length === 0) {
        delete updatedFacets[k];
      }
    });
    
    // Build URL
    const params = new URLSearchParams();
    if (categoryName) {
      params.append(categoryName, '');
    }
    
    const facetsParam = Object.entries(updatedFacets)
      .map(([f, vals]) => `${f}:${vals.join('~')}`)
      .join('|');
    
    if (facetsParam) {
      params.set('facets', facetsParam);
    }
    
    let queryString = params.toString();
    if (categoryName && queryString.includes(`${categoryName}=&`)) {
      queryString = queryString.replace(`${categoryName}=&`, `${categoryName}&`);
    } else if (categoryName && queryString.endsWith(`${categoryName}=`)) {
      queryString = queryString.replace(`${categoryName}=`, categoryName);
    }
    
    navigate(`${location.pathname}?${queryString}`, { replace: true });
  }, [selectedFacets, categoryName, navigate, location.pathname]);

  // Optimized search effect
  useEffect(() => {
    if (!categoryName) {
      setLoading(false);
      setError('Category name not provided.');
      return;
    }

    const fetchCategoryProducts = async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await search({
          query: '*:*',
          filterQueries,
          rows: 24,
          start: 0,
          facet: true,
          facetLimit: 50,
          facetMinCount: 1,
          queryProfile: 'category',
          collapse: true,
          collapseField: 'group',
          expand: true,
          expandField: 'image_url',
          signal
        });
        
        if (!mountedRef.current || signal.aborted) return;
        
        if (response?.success && response.documents) {
          // Process results to include variant images like FeaturedProducts
          const expanded = response.data?.expanded || {};
          
          const productsWithVariants = response.documents.map(product => {
            const groupId = product.group || product.id;
            const expandedDocs = expanded[groupId]?.docs || [];
            
            // Collect variant data with both image URLs and SKUs from expanded docs
            let variantData = expandedDocs
              .filter(doc => doc.image_url)
              .map(doc => ({
                image_url: doc.image_url,
                sku: doc.sku || doc.id,
                title: doc.title,
                id: doc.id,
                product_id: doc.product_id,
              }));
            
            // Collect all unique image URLs from expanded docs
            let variantImages = expandedDocs
              .map(doc => doc.image_url)
              .filter(Boolean);
            
            // Fallback: If no expanded docs and we have a group, search for other products in the same group
            if (variantImages.length === 0 && product.group && response.documents) {
              const sameGroupProducts = response.documents.filter(p => p.group === groupId && p.id !== product.id);
              variantImages = sameGroupProducts
                .map(p => p.image_url)
                .filter(Boolean);
              
              // Also collect variant data for fallback
              variantData = sameGroupProducts
                .filter(p => p.image_url)
                .map(p => ({
                  image_url: p.image_url,
                  sku: p.sku || p.id,
                  title: p.title,
                  id: p.id,
                  product_id: p.product_id,
                }));
            }
            
            // Always include the main product as first variant if not already included
            const mainVariant = {
              image_url: product.image_url,
              sku: product.sku || product.id,
              title: product.title,
              id: product.id,
              product_id: product.product_id,
            };
            
            if (product.image_url && !variantImages.includes(product.image_url)) {
              variantImages.unshift(product.image_url);
              variantData.unshift(mainVariant);
            }
            
            return {
              ...product,
              variant_images: variantImages,
              variant_data: variantData,
              total_variants: variantImages.length
            };
          });
          
          setProducts(productsWithVariants);
          setNumFound(response.totalResults || response.data?.response?.numFound || 0);
          setFacets(normalizeFacets(response.facets) || null);
        } else if (response?.data?.response?.docs) {
          setProducts(response.data.response.docs);
          setNumFound(response.data.response.numFound);
          setFacets(normalizeFacets(response.data.facet_counts) || null);
        } else {
          setProducts([]);
          setNumFound(0);
          setFacets(null);
        }
      } catch (err) {
        if (!signal.aborted && mountedRef.current) {
          setError(err.message || 'Failed to fetch products for this category.');
          setProducts([]);
          setNumFound(0);
        }
      } finally {
        if (mountedRef.current && !signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    // Debounce search calls
    const timeoutId = setTimeout(fetchCategoryProducts, 100);
    
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [categoryName, filterQueries]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Memoized clear filters handler
  const handleClearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    if (categoryName) {
      newParams.append(categoryName, '');
    }
    let queryString = newParams.toString();
    if (categoryName && queryString.endsWith(`${categoryName}=`)) {
      queryString = queryString.replace(`${categoryName}=`, categoryName);
    }
    navigate(`${location.pathname}?${queryString}`, { replace: true });
    setMobileFiltersOpen(false);
  }, [categoryName, navigate, location.pathname]);

  // Early returns for loading and error states
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="text" height={48} width="40%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={24} width="30%" sx={{ mb: 3 }} />
        
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
          </Box>
        )}
        
        <Grid container spacing={3} alignItems="flex-start">
          {/* Facet sidebar skeleton - Desktop only */}
          {!isMobile && (
            <Grid item xs={12} md={3} lg={2.5}>
              <Paper elevation={0} sx={{ 
                p: 3, 
                borderRadius: 2, 
                bgcolor: '#f8fafc', 
                border: '1px solid #eee', 
                position: 'sticky', 
                top: 24,
                width: '100%'
              }}>
                <FacetSidebarSkeleton />
              </Paper>
            </Grid>
          )}
          
          <Grid item xs={12} md={!isMobile ? 9 : 12} lg={!isMobile ? 9.5 : 12}>
            <ProductGrid products={[]} loading={true} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          Error loading products for {displayCategoryName}: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textTransform: 'capitalize' }}>
        {displayCategoryName}
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
        {numFound} product{numFound !== 1 ? 's' : ''} found
      </Typography>

      {/* Mobile filter button */}
      {isMobile && facets && (
        <Box sx={{ mb: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={() => setMobileFiltersOpen(true)}
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            Filter Results
          </Button>
        </Box>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        {/* Desktop facet sidebar */}
        {facets && !isMobile && (
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper elevation={0} sx={{ 
              p: 3, 
              borderRadius: 2, 
              bgcolor: '#f8fafc', 
              border: '1px solid #eee', 
              position: 'sticky', 
              top: 24,
              width: '100%'
            }}>
              <FacetSidebar 
                facets={facets} 
                selectedFacets={selectedFacets} 
                onFacetChange={handleFacetChange} 
              />
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={facets && !isMobile ? 9 : 12} lg={facets && !isMobile ? 9.5 : 12}>
          <ProductGrid products={products} loading={false} />
        </Grid>
      </Grid>

      {/* Mobile filters drawer */}
      <Drawer
        anchor="bottom"
        open={isMobile && mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ 
          sx: { 
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh'
          } 
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          borderBottom: '1px solid #eee' 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Filter Results</Typography>
          <IconButton onClick={() => setMobileFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, maxHeight: 'calc(90vh - 60px)', overflow: 'auto' }}>
          {facets && (
            <FacetSidebar 
              facets={facets} 
              selectedFacets={selectedFacets} 
              onFacetChange={handleFacetChange} 
            />
          )}
        </Box>
        <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            sx={{ flex: 1 }}
          >
            Clear Filters
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setMobileFiltersOpen(false)}
            sx={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </Container>
  );
}

export default CategoryPage;
